export type ThemeMode = 'light' | 'dark';

export type ToneResolver = number | ((seedTone: number) => number);

export interface TokenMapping {
  light: ToneResolver;
  dark: ToneResolver;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function filledTone(seedTone: number, mode: ThemeMode): number {
  return mode === 'dark'
    ? clamp(Math.round(seedTone) + 15, 45, 65)
    : clamp(Math.round(seedTone), 25, 50);
}

function onNeutralTone(seedTone: number, mode: ThemeMode): number {
  return mode === 'dark'
    ? clamp(Math.round(seedTone) + 35, 65, 90)
    : clamp(Math.round(seedTone), 20, 45);
}

function createFilledMapping(offset = 0): TokenMapping {
  return {
    light: (seedTone) => clamp(filledTone(seedTone, 'light') + offset, 5, 95),
    dark: (seedTone) => clamp(filledTone(seedTone, 'dark') + offset, 5, 95),
  };
}

function createOnNeutralMapping(offset = 0): TokenMapping {
  return {
    light: (seedTone) => clamp(onNeutralTone(seedTone, 'light') + offset, 5, 95),
    dark: (seedTone) => clamp(onNeutralTone(seedTone, 'dark') + offset, 5, 95),
  };
}

// Filled/primary surfaces use the configured seed literally; hover/active/subtle stay ramped.
export const primaryTokens: Record<'main' | 'button' | 'link', readonly string[]> = {
  main: ['--bg-brand', '--bg-top-bar'],
  button: ['--bg-button'],
  link: ['--text-link'],
};

export const mainTokens: Record<string, TokenMapping> = {
  '--bg-brand': createFilledMapping(),
  '--bg-brand-hover': createFilledMapping(+10),
  '--bg-brand-active': createFilledMapping(-10),
  '--bg-brand-subtle': { light: 80, dark: 20 },
  '--bg-brand-subtler': { light: 90, dark: 10 },
  '--bg-brand-subtlest': { light: 95, dark: 5 },
  '--bg-top-bar': {
    light: (seedTone) => clamp(Math.round(seedTone), 25, 50),
    dark: (seedTone) => clamp(Math.round(seedTone), 10, 25),
  },
  '--border-brand': createFilledMapping(),
  '--border-brand-subtle': { light: 80, dark: 30 },
  '--border-brand-subtler': { light: 90, dark: 20 },
  '--border-brand-subtlest': { light: 95, dark: 10 },
  '--icons-brand': createOnNeutralMapping(),
  '--icons-brand-hover': createOnNeutralMapping(+10),
  '--icons-brand-active': createOnNeutralMapping(-10),
};

export const buttonTokens: Record<string, TokenMapping> = {
  '--bg-button': createFilledMapping(),
  '--bg-button-hover': createFilledMapping(+10),
};

export const linkTokens: Record<string, TokenMapping> = {
  '--text-link': createOnNeutralMapping(),
  '--text-link-hover': createOnNeutralMapping(+10),
  '--text-link-active': createOnNeutralMapping(-10),
};
